import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { PermissionService } from '../services/permission.service';
import { LoggerService } from '../services/logger.service';
import {
  ALWAYS_VISIBLE_ROUTES,
  INTERNAL_ONLY_ROUTES,
  ROUTE_ACCESS_MAP,
} from '../config/access-area.config';

/**
 * Guard that checks access area permissions for routes.
 * Replaces the old PermissionGuard.
 *
 * Logic:
 * 1. Always-visible routes → allow
 * 2. Internal-only routes → check isInternal() (or specific role)
 * 3. Internal users → allow (they see all pages)
 * 4. Hotel users → check ROUTE_ACCESS_MAP for required access area
 * 5. Unmapped routes → deny + log warning (fail-safe)
 *
 * Usage in routes:
 * ```typescript
 * { path: 'bookings', component: BookingsComponent, canActivate: [AccessAreaGuard] }
 * ```
 * No `data: { permissions: [...] }` needed — the guard reads from config.
 */
export const AccessAreaGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): boolean | UrlTree => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);
  const logger = inject(LoggerService);

  const routePath = route.routeConfig?.path || '';

  // 1. Always-visible routes
  if (ALWAYS_VISIBLE_ROUTES.includes(routePath)) {
    return true;
  }

  // 2. Internal-only routes
  if (routePath in INTERNAL_ONLY_ROUTES) {
    if (!permissionService.isInternal()) {
      logger.warn(`Hotel user blocked from internal-only route: ${routePath}`);
      return router.createUrlTree(['/access-denied']);
    }

    const restriction = INTERNAL_ONLY_ROUTES[routePath];
    // Check for specific role restriction (e.g., extranet-users requires Super Admin)
    if (typeof restriction === 'string') {
      const userRole = permissionService.getInternalRole();
      if (userRole !== restriction) {
        logger.warn(`Internal user with role "${userRole}" blocked from "${routePath}" (requires "${restriction}")`);
        return router.createUrlTree(['/access-denied']);
      }
    }

    return true;
  }

  // 3. Internal users can access all mapped routes
  if (permissionService.isInternal()) {
    return true;
  }

  // 4. Hotel users: look up access area requirement
  const requiredArea = ROUTE_ACCESS_MAP[routePath];
  if (requiredArea) {
    if (permissionService.hasAccessArea(requiredArea)) {
      return true;
    }

    logger.warn(`Access denied for route "${routePath}". Required area: ${requiredArea}`);
    return router.createUrlTree(['/access-denied']);
  }

  // 5. Unmapped route — deny (fail-safe)
  logger.warn(`Route "${routePath}" not found in access config. Denying access (fail-safe).`);
  return router.createUrlTree(['/access-denied']);
};
